/**
 * AdminView Component
 * Extracted from HubDashboard.jsx
 * Handles: System settings, role management, points config, activity logs, security
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ShieldCheck, Shield, Sliders, Activity, Lock, Plus, ChevronLeft, ChevronRight,
  RefreshCw, AlertTriangle, FileText, Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPS = [
  { id: 'ridero', name: 'Ridero' },
  { id: 'mechanic_kiosk', name: 'Kiosk' },
  { id: 'pti_pector', name: 'PTI' },
  { id: 'mcanick_scout', name: 'Mcnk Ai' },
  { id: 'mr_mathematico', name: 'Mr.Math' },
  { id: 'fleet_share', name: 'Fleet Share' },
];

export default function AdminView() {
  const [activePanel, setActivePanel] = useState(null);
  const [settings, setSettings] = useState({
    company_name: 'Leader Hub',
    email_domains: '',
    maintenance_mode: false,
    allow_signup: true
  });
  const [roles, setRoles] = useState([]);
  const [logs, setLogs] = useState([]);
  const [securityLogs, setSecurityLogs] = useState([]);
  const [lockoutEmail, setLockoutEmail] = useState('');
  const [lockoutStatus, setLockoutStatus] = useState(null);
  const [pointsConfig, setPointsConfig] = useState({
    login_points: 10,
    referral_points: 50,
    training_completion: 100,
    perfect_inspection: 25
  });
  const [loading, setLoading] = useState(false);
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [newRoleData, setNewRoleData] = useState({ name: '', level: 30, color: '#64748B', department: 'general' });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        if (activePanel === 'roles') {
          const res = await axios.get(`${API}/roles`, { withCredentials: true });
          setRoles(res.data);
        } else if (activePanel === 'logs') {
          const res = await axios.get(`${API}/admin/logs`, { withCredentials: true });
          setLogs(res.data);
        } else if (activePanel === 'settings') {
          const res = await axios.get(`${API}/admin/settings`, { withCredentials: true });
          if (res.data) setSettings(res.data);
        } else if (activePanel === 'points') {
          const res = await axios.get(`${API}/admin/points-config`, { withCredentials: true });
          if (res.data) setPointsConfig(res.data);
        } else if (activePanel === 'security') {
          const res = await axios.get(`${API}/auth/security/logs?limit=50`, { withCredentials: true });
          if (res.data?.logs) setSecurityLogs(res.data.logs);
        }
      } catch (e) {
        console.error('Failed to load admin data');
      } finally {
        setLoading(false);
      }
    };
    if (activePanel) loadData();
  }, [activePanel]);

  const saveSettings = async () => {
    try {
      await axios.post(`${API}/admin/settings`, settings, { withCredentials: true });
      toast.success('Settings saved successfully');
    } catch (e) {
      toast.error('Failed to save settings');
    }
  };

  const savePointsConfig = async () => {
    try {
      await axios.post(`${API}/admin/points-config`, pointsConfig, { withCredentials: true });
      toast.success('Points configuration saved');
    } catch (e) {
      toast.error('Failed to save points config');
    }
  };

  const createRole = async () => {
    if (!newRoleData.name) { toast.error('Role name required'); return; }
    try {
      await axios.post(`${API}/roles`, newRoleData, { withCredentials: true });
      toast.success('Role created!');
      setShowCreateRoleModal(false);
      setNewRoleData({ name: '', level: 30, color: '#64748B', department: 'general' });
      const res = await axios.get(`${API}/roles`, { withCredentials: true });
      setRoles(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create role');
    }
  };

  const updateRole = async () => {
    try {
      await axios.put(`${API}/roles/${selectedRole.role_id}`, {
        name: selectedRole.name,
        level: selectedRole.level,
        color: selectedRole.color,
        department: selectedRole.department
      }, { withCredentials: true });
      toast.success('Role updated!');
      setShowEditRoleModal(false);
      const res = await axios.get(`${API}/roles`, { withCredentials: true });
      setRoles(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update role');
    }
  };

  const deleteRole = async () => {
    if (!window.confirm('Delete this role?')) return;
    try {
      await axios.delete(`${API}/roles/${selectedRole.role_id}`, { withCredentials: true });
      toast.success('Role deleted');
      setShowEditRoleModal(false);
      const res = await axios.get(`${API}/roles`, { withCredentials: true });
      setRoles(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete role');
    }
  };

  const checkLockout = async () => {
    if (!lockoutEmail) return;
    try {
      const res = await axios.get(`${API}/auth/security/lockout-status?email=${lockoutEmail}`, { withCredentials: true });
      setLockoutStatus(res.data);
    } catch (e) {
      toast.error('Failed to check lockout status');
    }
  };

  const unlockAccount = async () => {
    try {
      await axios.post(`${API}/auth/security/unlock-account?email=${lockoutEmail}`, {}, { withCredentials: true });
      toast.success('Account unlocked!');
      setLockoutStatus({...lockoutStatus, is_locked: false, failed_attempts: 0});
    } catch (e) {
      toast.error('Failed to unlock account');
    }
  };

  if (activePanel) {
    return (
      <div className="space-y-6">
        <button onClick={() => setActivePanel(null)} className="flex items-center gap-2 text-accent hover:underline">
          <ChevronLeft className="w-4 h-4" /> Back to Admin
        </button>

        {loading ? (
          <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-accent" /></div>
        ) : (
          <>
            {/* Settings Panel */}
            {activePanel === 'settings' && (
              <div className="bg-card/60 rounded-2xl p-6 border border-border/30 space-y-6">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <ShieldCheck className="w-6 h-6 text-purple-400" /> System Settings
                </h2>
                <div className="grid gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground">Company Name</label>
                    <input type="text" value={settings.company_name}
                      onChange={e => setSettings({...settings, company_name: e.target.value})}
                      className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-xl bg-secondary">
                    <div>
                      <p className="font-medium">Allow New Signups</p>
                      <p className="text-sm text-muted-foreground">Enable user registration</p>
                    </div>
                    <button onClick={() => setSettings({...settings, allow_signup: !settings.allow_signup})}
                      className={`w-12 h-6 rounded-full transition-colors ${settings.allow_signup ? 'bg-green-500' : 'bg-gray-600'}`}>
                      <div className={`w-5 h-5 rounded-full bg-white transform transition-transform ${settings.allow_signup ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-xl bg-secondary">
                    <div>
                      <p className="font-medium">Maintenance Mode</p>
                      <p className="text-sm text-muted-foreground">Disable access for non-admins</p>
                    </div>
                    <button onClick={() => setSettings({...settings, maintenance_mode: !settings.maintenance_mode})}
                      className={`w-12 h-6 rounded-full transition-colors ${settings.maintenance_mode ? 'bg-orange-500' : 'bg-gray-600'}`}>
                      <div className={`w-5 h-5 rounded-full bg-white transform transition-transform ${settings.maintenance_mode ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
                <button onClick={saveSettings} className="w-full py-3 bg-accent text-black rounded-xl font-bold">Save Settings</button>
              </div>
            )}

            {/* Roles Panel */}
            {activePanel === 'roles' && (
              <div className="bg-card/60 rounded-2xl p-6 border border-border/30 space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <Shield className="w-6 h-6 text-blue-400" /> Role Management
                  </h2>
                  <button onClick={() => setShowCreateRoleModal(true)}
                    className="px-4 py-2 bg-accent text-black rounded-xl font-medium flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Add Role
                  </button>
                </div>
                <div className="space-y-3">
                  {roles.map(role => (
                    <div key={role.role_id} onClick={() => { setSelectedRole(role); setShowEditRoleModal(true); }}
                      className="p-4 rounded-xl bg-secondary hover:bg-secondary/70 cursor-pointer">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: role.color || '#64748B' }} />
                          <div>
                            <p className="font-medium">{role.name || role.role_id}</p>
                            <p className="text-sm text-muted-foreground">Level {role.level || 0}</p>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Points Panel */}
            {activePanel === 'points' && (
              <div className="bg-card/60 rounded-2xl p-6 border border-border/30 space-y-6">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Sliders className="w-6 h-6 text-green-400" /> Points Configuration
                </h2>
                <div className="grid gap-4">
                  {[
                    { key: 'login_points', label: 'Daily Login Bonus', desc: 'Points for logging in each day' },
                    { key: 'referral_points', label: 'Referral Bonus', desc: 'Points for successful referrals' },
                    { key: 'training_completion', label: 'Training Completion', desc: 'Points for completing training' },
                    { key: 'perfect_inspection', label: 'Perfect Inspection', desc: 'Points for passing inspection' }
                  ].map(({ key, label, desc }) => (
                    <div key={key} className="p-4 rounded-xl bg-secondary">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium">{label}</p>
                        <input type="number" value={pointsConfig[key]}
                          onChange={e => setPointsConfig({...pointsConfig, [key]: parseInt(e.target.value) || 0})}
                          className="w-24 px-3 py-2 rounded-lg bg-background border border-border text-right"
                        />
                      </div>
                      <p className="text-sm text-muted-foreground">{desc}</p>
                    </div>
                  ))}
                </div>
                <button onClick={savePointsConfig} className="w-full py-3 bg-accent text-black rounded-xl font-bold">Save Points Config</button>
              </div>
            )}

            {/* Logs Panel */}
            {activePanel === 'logs' && (
              <div className="bg-card/60 rounded-2xl p-6 border border-border/30 space-y-6">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Activity className="w-6 h-6 text-orange-400" /> Activity Logs
                </h2>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {logs.map((log, i) => (
                    <div key={i} className="p-3 rounded-xl bg-secondary flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 ${log.type === 'error' ? 'bg-red-500' : log.type === 'warning' ? 'bg-yellow-500' : 'bg-green-500'}`} />
                      <div className="flex-1">
                        <p className="text-sm">{log.message}</p>
                        <p className="text-xs text-muted-foreground mt-1">{new Date(log.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                  {logs.length === 0 && <p className="text-center py-8 text-muted-foreground">No activity logs yet</p>}
                </div>
              </div>
            )}

            {/* Security Panel */}
            {activePanel === 'security' && (
              <div className="bg-card/60 rounded-2xl p-6 border border-border/30 space-y-6">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Lock className="w-6 h-6 text-red-400" /> Security Center
                </h2>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                    <p className="text-2xl font-bold text-green-400">{securityLogs.filter(l => l.event_type === 'login_success').length}</p>
                    <p className="text-sm text-muted-foreground">Successful</p>
                  </div>
                  <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                    <p className="text-2xl font-bold text-red-400">{securityLogs.filter(l => l.event_type.includes('failed')).length}</p>
                    <p className="text-sm text-muted-foreground">Failed</p>
                  </div>
                  <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                    <p className="text-2xl font-bold text-yellow-400">{securityLogs.filter(l => l.event_type.includes('locked')).length}</p>
                    <p className="text-sm text-muted-foreground">Locked</p>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-secondary space-y-3">
                  <h3 className="font-medium flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-400" /> Check Account Lockout
                  </h3>
                  <div className="flex gap-2">
                    <input type="email" placeholder="Enter email..." value={lockoutEmail}
                      onChange={e => setLockoutEmail(e.target.value)}
                      className="flex-1 px-4 py-2 rounded-xl bg-background border border-border"
                    />
                    <button onClick={checkLockout} className="px-4 py-2 bg-accent text-black rounded-xl font-medium">Check</button>
                  </div>
                  {lockoutStatus && (
                    <div className={`p-3 rounded-lg ${lockoutStatus.is_locked ? 'bg-red-500/20' : 'bg-green-500/20'}`}>
                      <p className="font-medium">{lockoutStatus.email}</p>
                      <p className="text-sm">{lockoutStatus.is_locked ? `🔒 LOCKED` : '✅ Not locked'}</p>
                      {lockoutStatus.is_locked && (
                        <button onClick={unlockAccount} className="mt-2 px-3 py-1 bg-green-500 text-white rounded-lg text-sm">Unlock</button>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {securityLogs.slice(0, 20).map((log, i) => (
                    <div key={i} className="p-3 rounded-xl bg-secondary flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 ${log.event_type.includes('success') ? 'bg-green-500' : 'bg-red-500'}`} />
                      <div className="flex-1">
                        <span className="text-xs px-2 py-0.5 rounded bg-secondary">{log.event_type}</span>
                        <p className="text-sm mt-1">{log.email || 'N/A'}</p>
                        <p className="text-xs text-muted-foreground">IP: {log.ip_address || 'N/A'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Create Role Modal */}
        {showCreateRoleModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowCreateRoleModal(false)}>
            <div className="bg-card rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
              <h3 className="text-xl font-bold mb-4">Create Role</h3>
              <div className="space-y-4">
                <input type="text" placeholder="Role Name" value={newRoleData.name}
                  onChange={e => setNewRoleData({...newRoleData, name: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-secondary border border-border"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input type="number" placeholder="Level" value={newRoleData.level}
                    onChange={e => setNewRoleData({...newRoleData, level: parseInt(e.target.value) || 30})}
                    className="px-4 py-3 rounded-xl bg-secondary border border-border"
                  />
                  <input type="color" value={newRoleData.color}
                    onChange={e => setNewRoleData({...newRoleData, color: e.target.value})}
                    className="h-12 rounded-xl cursor-pointer"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowCreateRoleModal(false)} className="flex-1 py-3 bg-secondary rounded-xl">Cancel</button>
                <button onClick={createRole} className="flex-1 py-3 bg-accent text-black rounded-xl font-bold">Create</button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Role Modal */}
        {showEditRoleModal && selectedRole && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowEditRoleModal(false)}>
            <div className="bg-card rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
              <h3 className="text-xl font-bold mb-4">Edit Role: {selectedRole.name}</h3>
              <div className="space-y-4">
                <input type="text" value={selectedRole.name}
                  onChange={e => setSelectedRole({...selectedRole, name: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-secondary border border-border"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input type="number" value={selectedRole.level}
                    onChange={e => setSelectedRole({...selectedRole, level: parseInt(e.target.value) || 30})}
                    className="px-4 py-3 rounded-xl bg-secondary border border-border"
                  />
                  <input type="color" value={selectedRole.color || '#64748B'}
                    onChange={e => setSelectedRole({...selectedRole, color: e.target.value})}
                    className="h-12 rounded-xl cursor-pointer"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                {selectedRole.is_custom && (
                  <button onClick={deleteRole} className="px-4 py-3 bg-red-500/20 text-red-400 rounded-xl">Delete</button>
                )}
                <button onClick={() => setShowEditRoleModal(false)} className="flex-1 py-3 bg-secondary rounded-xl">Cancel</button>
                <button onClick={updateRole} className="flex-1 py-3 bg-accent text-black rounded-xl font-bold">Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Main Admin Panel Grid
  return (
    <div className="grid grid-cols-2 gap-6">
      {[
        { panel: 'settings', icon: ShieldCheck, color: 'purple', title: 'Admin Controls', desc: 'System settings' },
        { panel: 'roles', icon: Shield, color: 'blue', title: 'Role Management', desc: 'Configure roles' },
        { panel: 'points', icon: Sliders, color: 'green', title: 'Points Config', desc: 'Manage points' },
        { panel: 'logs', icon: Activity, color: 'orange', title: 'Activity Logs', desc: 'View logs' },
        { panel: 'security', icon: Lock, color: 'red', title: 'Security', desc: 'Security center' }
      ].map(({ panel, icon: Icon, color, title, desc }) => (
        <div key={panel} onClick={() => setActivePanel(panel)}
          className="bg-card/60 rounded-2xl p-6 border border-border/30 flex items-center gap-4 hover:bg-accent/5 cursor-pointer">
          <div className={`w-14 h-14 rounded-xl bg-${color}-500/20 flex items-center justify-center`}>
            <Icon className={`w-7 h-7 text-${color}-400`} />
          </div>
          <div>
            <p className="font-bold text-lg">{title}</p>
            <p className="text-sm text-muted-foreground">{desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
